import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import { BarChart3, CalendarClock, CheckCheck, Clock, Users, X } from "lucide-react";
import {
  REPORT_GROUP_BY,
  REPORT_GROUP_BY_LABEL,
  REPORT_SCOPES,
  REPORT_SCOPE_LABEL,
  addDays,
  businessDate,
  isValidYmd,
  money,
  monthBounds,
  seesMoney,
  startOfWeek,
  yearMonth,
  ymd,
  type HoursReportDTO,
  type MoneyByCurrency,
  type ReportBucket,
  type ReportGroupBy,
  type ReportScope,
  type ReportsDTO,
} from "@contractorhub/shared";
import {
  adminContractorsQuery,
  adminProjectsQuery,
  adminReportsQuery,
} from "../../../lib/queries";
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SegmentedControl,
  Select,
  StatCard,
} from "../../../components/ui";
import { RankedBars } from "../../../components/charts/ranked-bars";
import { ColumnChart, type ColumnDatum } from "../../../components/chart";
import { apiDownload } from "../../../lib/api";
import { ExportButton } from "../../../components/export-button";
import { EntityFilterFields } from "../../../components/entity-filter-fields";
import { ContractorMonthReportCard } from "../../../components/contractor-month-report-card";
import { GstReportCard } from "../../../components/gst-report-card";

const searchSchema = z.object({
  groupBy: z.enum(REPORT_GROUP_BY).default("client"),
  scope: z.enum(REPORT_SCOPES).default("APPROVED_AND_SUBMITTED"),
  from: z.string().refine(isValidYmd).optional(),
  to: z.string().refine(isValidYmd).optional(),
  // The API has always accepted these (they ride `timesheetFilterShape`); only
  // the screen was dates-only, so a report could not be narrowed to the
  // contractor or client someone was actually asking about.
  contractorId: z.string().optional(),
  projectId: z.string().optional(),
  clientId: z.string().optional(),
});

export const Route = createFileRoute("/_authed/_staff/reports")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminReportsQuery(deps)),
      context.queryClient.ensureQueryData(adminContractorsQuery),
      context.queryClient.ensureQueryData(adminProjectsQuery),
    ]),
  component: ReportsPage,
});

/** Past this many weeks the columns become slivers, so they roll up to months. */
const MAX_WEEK_COLUMNS = 26;

/** How many ranked rows before the tail is folded behind a toggle. */
const RANKED_LIMIT = 10;

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

type PeriodPreset = "thisWeek" | "lastWeek" | "thisMonth" | "lastMonth";

const PERIOD_PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: "thisWeek", label: "This week" },
  { key: "lastWeek", label: "Last week" },
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
];

/**
 * The `from`/`to` a preset stands for, as of the business's today.
 *
 * A preset is only ever a way of filling in the two dates — the URL carries the
 * dates, not the preset — so a shared link keeps meaning the range it was sent
 * with, and a preset shows as selected exactly while the dates still match it.
 */
function presetRange(preset: PeriodPreset, today: Date): { from: string; to: string } {
  const monday = startOfWeek(today);
  switch (preset) {
    case "thisWeek":
      return { from: ymd(monday), to: ymd(addDays(monday, 6)) };
    case "lastWeek":
      return { from: ymd(addDays(monday, -7)), to: ymd(addDays(monday, -1)) };
    case "thisMonth": {
      const { first, last } = monthBounds(yearMonth(today));
      return { from: ymd(first), to: ymd(last) };
    }
    case "lastMonth": {
      // The day before the 1st is always in the previous month.
      const { first } = monthBounds(yearMonth(today));
      const previous = monthBounds(yearMonth(addDays(first, -1)));
      return { from: ymd(previous.first), to: ymd(previous.last) };
    }
  }
}

/**
 * Value, rendered per currency.
 *
 * There is no combined figure by design: one entity bills AUD and one INR, and
 * a single number would have to pick a symbol and be wrong for one of them.
 * Nothing priced renders as an em dash rather than `$0` — unpriced work is
 * missing data, not free work (RATE-9).
 */
function Money({ value }: { value: MoneyByCurrency }) {
  if (value.length === 0) return <span className="text-subtle">—</span>;
  return (
    <span className="tabular-nums">
      {value.map((v) => money(v.value, v.currency)).join(" · ")}
    </span>
  );
}

/**
 * Whether the report carries money. A user admin's does not (AUTH-13): the API
 * leaves every `value` and `unpricedHours` out and ranks by hours instead.
 */
function isPricedReport(report: ReportsDTO | HoursReportDTO): report is ReportsDTO {
  return "value" in report.totals;
}

/** A bucket from either report; the money is there only on a priced one. */
type Bucket = HoursReportDTO["buckets"][number] &
  Partial<Pick<ReportBucket, "value" | "unpricedHours">>;

function ReportsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { user } = Route.useRouteContext();
  const fullAdmin = seesMoney(user.role);
  const { data } = useSuspenseQuery(adminReportsQuery(search));
  const priced = isPricedReport(data) ? data : null;
  const buckets: Bucket[] = data.buckets;
  const { data: contractorData } = useSuspenseQuery(adminContractorsQuery);
  const { data: projectData } = useSuspenseQuery(adminProjectsQuery);
  const [showAllBuckets, setShowAllBuckets] = useState(false);

  // `ProjectOption` carries no `clientId`, so the project list comes from the
  // projects endpoint rather than the contractor rows — same shape the queue uses.
  const contractorOptions = contractorData.contractors.map((contractor) => ({
    id: contractor.id,
    name: contractor.name,
  }));
  const projectOptions = projectData.projects.map((project) => ({
    id: project.id,
    name: project.name,
    clientId: project.clientId,
    clientName: project.clientName,
  }));

  const patch = (next: Partial<typeof search>) =>
    void navigate({ to: "/reports", search: { ...search, ...next } });

  // Resolved once per render, so every preset is measured against the same day.
  const today = businessDate();
  const activePreset = PERIOD_PRESETS.find(({ key }) => {
    const range = presetRange(key, today);
    return range.from === search.from && range.to === search.to;
  })?.key;

  /*
   * Roll long ranges up to months.
   *
   * A two-year range is 104 weekly columns; at any card width those are slivers
   * nobody can read or point at. Months are grouped by the week's *start* month,
   * so a week straddling a month boundary counts to the month it began in — a
   * rounding the chart states in its subtitle rather than hiding.
   */
  const { columns, rolledUp } = useMemo(() => {
    const weeks: ColumnDatum[] = data.weeks.map((week) => ({
      key: week.weekStart,
      label: week.label,
      value: week.hours,
    }));
    if (weeks.length <= MAX_WEEK_COLUMNS) return { columns: weeks, rolledUp: false };

    const byMonth = new Map<string, ColumnDatum>();
    for (const week of data.weeks) {
      const date = new Date(week.weekStart);
      const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
      const existing = byMonth.get(key);
      if (existing) {
        existing.value += week.hours;
      } else {
        byMonth.set(key, {
          key,
          label: `${MONTHS[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(2)}`,
          value: week.hours,
        });
      }
    }
    return { columns: [...byMonth.values()], rolledUp: true };
  }, [data.weeks]);

  const visibleBuckets = showAllBuckets ? buckets : buckets.slice(0, RANKED_LIMIT);

  return (
    <div>
      <PageHeader
        title="Reports"
        description={
          priced
            ? "Hours and indicative billings across contractors and clients."
            : "Hours across contractors and clients."
        }
        actions={
          <ExportButton
            // Same filters, `status=ALL`: the detail grain of whatever is on
            // screen. It used to carry only the dates, so narrowing to one client
            // and exporting handed back every client.
            download={() =>
              apiDownload("GET /api/manage/exports/timesheets", {
                query: {
                  from: search.from,
                  to: search.to,
                  contractorId: search.contractorId,
                  clientId: search.clientId,
                  projectId: search.projectId,
                  status: "ALL",
                },
              })
            }
          />
        }
      />

      {/* One filter row above everything it scopes — never per-chart filters. */}
      <Card className="mb-6 p-3">
        {/* Two rows: *what* is being reported on, then *when* and how it is read. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <EntityFilterFields
            filters={search}
            contractors={contractorOptions}
            projects={projectOptions}
            onChange={patch}
          />
          <Field label="Include">
            <Select
              value={search.scope}
              onChange={(e) => patch({ scope: e.target.value as ReportScope })}
            >
              {REPORT_SCOPES.map((option) => (
                <option key={option} value={option}>
                  {REPORT_SCOPE_LABEL[option]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-12">
          <Field label="Period" group className="sm:col-span-2 lg:col-span-4">
            <SegmentedControl
              label="Period"
              options={PERIOD_PRESETS}
              value={activePreset}
              onChange={(preset) => patch(presetRange(preset, today))}
            />
          </Field>
          <Field label="From" className="lg:col-span-2">
            <Input
              type="date"
              value={search.from ?? ""}
              onChange={(e) => patch({ from: e.target.value || undefined })}
            />
          </Field>
          <Field label="To" className="lg:col-span-2">
            <Input
              type="date"
              value={search.to ?? ""}
              onChange={(e) => patch({ to: e.target.value || undefined })}
            />
          </Field>
          {/* Four labels ("Contractor", "Project") do not fit a quarter-width
              segmented control and were truncating to "Cont…". */}
          <Field label="Group by" group className="sm:col-span-2 lg:col-span-4">
            <SegmentedControl
              label="Group by"
              options={REPORT_GROUP_BY.map((key) => ({
                key,
                label: REPORT_GROUP_BY_LABEL[key],
              }))}
              value={search.groupBy}
              onChange={(groupBy) => patch({ groupBy })}
            />
          </Field>
        </div>
        {/* Clears what narrows the data, and deliberately not `groupBy`/`scope`:
            those are how the report is being read, not what it is reading. */}
        {(search.from ||
          search.to ||
          search.contractorId ||
          search.clientId ||
          search.projectId) && (
          <div className="mt-3 flex justify-end border-t border-line pt-3">
            <Button
              variant="ghost"
              size="sm"
              icon={X}
              onClick={() =>
                patch({
                  from: undefined,
                  to: undefined,
                  contractorId: undefined,
                  clientId: undefined,
                  projectId: undefined,
                })
              }
            >
              Clear filters
            </Button>
          </div>
        )}
      </Card>

      <div
        className={`mb-6 grid grid-cols-2 gap-4 ${priced ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}
      >
        <StatCard label="Total hours" value={data.totals.hours.toFixed(0)} icon={Clock} />
        {priced && (
          <StatCard
            label="Indicative billings"
            // Approved weeks are priced at the rate they were approved against
            // (RATE-5); weeks still in flight, at the rate in force on their Monday.
            // Neither moves when someone's rate changes today.
            value={
              priced.totals.value.length === 0
                ? "—"
                : priced.totals.value.map((v) => money(v.value, v.currency)).join(" · ")
            }
            icon={CheckCheck}
            tone="success"
            hint={
              priced.totals.unpricedHours > 0
                ? `${priced.totals.unpricedHours.toFixed(0)}h not priced`
                : "Approved at their frozen rate"
            }
          />
        )}
        {/* Both hints say "in this view" because both now follow the filters —
            the dashboard is where the workspace-wide figures live. */}
        <StatCard
          label="Pending approvals"
          value={data.pending}
          hint="Awaiting review in this view"
          icon={CalendarClock}
          tone="warning"
        />
        <StatCard
          label="Active contractors"
          value={data.activeContractors}
          hint="With hours in this view"
          icon={Users}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title={rolledUp ? "Hours by month" : "Hours by week"}
            description={
              rolledUp
                ? `${REPORT_SCOPE_LABEL[data.scope]} timesheets · weeks rolled up to the month they start in`
                : `${REPORT_SCOPE_LABEL[data.scope]} timesheets`
            }
          />
          <div className="p-5">
            <ColumnChart
              data={columns}
              emptyLabel="No timesheet data in this range."
              tableCaption={rolledUp ? "Hours by month" : "Hours by week"}
              columnHeader={rolledUp ? "Month" : "Week"}
            />
          </div>
        </Card>

        <Card>
          <CardHeader
            title={`By ${REPORT_GROUP_BY_LABEL[data.groupBy].toLowerCase()}`}
            description={priced ? "Indicative, from agreed rates" : "Hours, most first"}
          />
          <div className="p-5">
            {buckets.length === 0 ? (
              <EmptyState
                icon={BarChart3}
                title="No data yet"
                description="Approved or submitted weeks in this range will appear here."
              />
            ) : (
              <>
                {/* Drilldown costs nothing server-side: the queue already reads
                    its whole filter state from the URL. The screen's own
                    narrowing travels with it, then the bucket wins on its own
                    axis, so clicking a client while filtered to one contractor
                    opens that contractor at that client. Hours no rate window
                    covers are reported, never folded into the total as $0. */}
                <RankedBars
                  items={visibleBuckets.map((bucket) => ({
                    key: bucket.key,
                    label: bucket.label,
                    measure: bucket.hours,
                    measureLabel: `${bucket.hours.toFixed(1)}h`,
                    aside: bucket.value ? <Money value={bucket.value} /> : undefined,
                    note:
                      bucket.unpricedHours ? (
                        <span className="text-micro text-warning-on-soft">
                          {bucket.unpricedHours.toFixed(0)}h not priced
                        </span>
                      ) : undefined,
                    link: {
                      to: "/timesheets",
                      search: {
                        status: "ALL",
                        from: search.from,
                        to: search.to,
                        contractorId: search.contractorId,
                        clientId: search.clientId,
                        projectId: search.projectId,
                        ...bucketFilter(data.groupBy, bucket.key),
                      },
                    },
                  }))}
                />

                {/* The tail folds behind a toggle rather than rendering 200 rows
                    into a narrow card — and stays reachable, because every row is
                    a real entity with its own drilldown. */}
                {buckets.length > RANKED_LIMIT && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3"
                    onClick={() => setShowAllBuckets((current) => !current)}
                  >
                    {showAllBuckets ? "Show top 10" : `Show all ${buckets.length}`}
                  </Button>
                )}

                {/* "Show all N" is only the whole truth while the server sent
                    everything. Past the page cap it is the top N — by value, or
                    by hours on a report without money — and saying so beats a
                    list that quietly ends. */}
                {data.pageInfo.total > buckets.length && (
                  <p className="mt-2 text-xs text-subtle">
                    Showing the top {buckets.length} of {data.pageInfo.total} by{" "}
                    {priced ? "value" : "hours"} — narrow the filters to see the rest.
                  </p>
                )}
              </>
            )}
          </div>
        </Card>
      </div>

      <ContractorMonthReportCard contractors={contractorData.contractors} />
      {/* GST is money end to end and its route stays admin-only; not rendering
          the card is what keeps its query from firing for a user admin. */}
      {fullAdmin && <GstReportCard />}
    </div>
  );
}

/** Which queue filter a bucket drills into. A week bucket pins the week itself. */
function bucketFilter(groupBy: ReportGroupBy, key: string) {
  switch (groupBy) {
    case "client":
      return { clientId: key };
    case "project":
      return { projectId: key };
    case "contractor":
      return { contractorId: key };
    case "week":
      return { weekStart: key };
  }
}
